import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Instagram, Youtube, Music, Linkedin, BadgeCheck, ArrowRight, Sparkles } from "lucide-react";

// SocialConnectBanner — sits at the top of the Profile Audience section.
// Surfaces the four supported social platforms; only YouTube currently has
// full OAuth (Feb 2026). The others ship as "Coming soon" placeholders so
// athletes see the verified-vs-unverified delineation up front.
//
// Connection status is fetched from the existing /social/status endpoint
// which already gates the YouTube OAuth flow used in Settings.

const PLATFORMS = [
  { key: "youtube", label: "YouTube", icon: Youtube, ready: true },
  { key: "instagram", label: "Instagram", icon: Instagram, ready: false },
  { key: "tiktok", label: "TikTok", icon: Music, ready: false },
  { key: "linkedin", label: "LinkedIn", icon: Linkedin, ready: false },
];

export default function SocialConnectBanner() {
  const [status, setStatus] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/social/status").then(({ data }) => setStatus(data || {})).catch(() => {});
  }, []);

  const connectYouTube = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/oauth/youtube/start");
      if (data?.auth_url) window.location.href = data.auth_url;
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-volt/40 bg-volt/5 p-5 md:p-6 mb-6" data-testid="social-connect-banner">
      <div className="flex items-start gap-3 mb-5">
        <Sparkles className="w-5 h-5 text-volt shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow text-volt">Verify your audience</div>
          <h3 className="font-heading text-lg md:text-xl uppercase tracking-tight mt-1">Connect your socials to unlock verified data.</h3>
          <p className="text-sm text-ink-muted mt-2 leading-relaxed max-w-2xl">
            Brands filter on verified follower count, engagement and demographics. Connecting drops your audience stats straight into your media kit and Brand-Pro filters — no manual entry, no guessing.
          </p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {PLATFORMS.map(p => {
          const Icon = p.icon;
          const connected = !!status?.[p.key]?.connected;
          return (
            <div key={p.key} data-testid={`social-connect-${p.key}`}
              className={`border p-4 flex flex-col gap-3 ${connected ? "border-volt bg-volt/5" : "border-line bg-bg"}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-white" />
                  <span className="font-heading text-sm uppercase tracking-tight">{p.label}</span>
                </div>
                {connected && <BadgeCheck className="w-4 h-4 text-volt" />}
              </div>
              {p.ready ? (
                connected ? (
                  <div className="text-xs text-volt uppercase tracking-widest" data-testid={`social-${p.key}-connected`}>
                    Connected
                  </div>
                ) : (
                  <button onClick={connectYouTube} disabled={busy}
                    data-testid={`social-${p.key}-connect-btn`}
                    className="btn-volt text-xs inline-flex items-center justify-center gap-1.5">
                    Connect {p.label} <ArrowRight className="w-3 h-3" />
                  </button>
                )
              ) : (
                <div className="text-[10px] uppercase tracking-widest text-ink-muted"
                  data-testid={`social-${p.key}-soon`}>Coming soon</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
